import { PermissionTooltip } from '@components/PermissionTooltip';
import { useAuth } from '@contexts/auth';
import { useSidebar } from '@contexts/sidebar';
import { FlatButton, HoverBorderButton } from '@elements/Button';
import { cn } from '@skupreme/shared/ui';
import { MuteText } from '@elements/Common';
import { isActionDisabled, isActionHidden } from '@hooks/url-permission';
import { DYNAMIC_INFORMATION } from '@page-components/marketplace-integration';
import { Badge, Tooltip } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  MARKETPLACE_LIMITS,
  sheetTypes,
  tooltipTexts,
} from '@utility/constants/common';
import OrganizationsService from '@utility/services/organization';
import { ToastMessage } from '@utility/toast';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';

const showNumber = value => CommonUtility.getFixNumber(Number(value) || 0);

function PropertyRow({ children }: { children: ReactNode }) {
  return (
    <div className="sk:mb-[5px] sk:flex sk:items-start sk:justify-between">
      {children}
    </div>
  );
}

function PropertyLabel({ children }: { children: ReactNode }) {
  return (
    <MuteText className="sk:text-left sk:whitespace-nowrap">
      {children}
    </MuteText>
  );
}

/* Marketplace icons ride on the metadata payload, which IMetadata does not declare. */
type MarketplaceIcons = Record<string, { icon?: string } | undefined>;

export const getMarketplaceIcon = (
  metadata: object | undefined,
  key: string,
): string => {
  const marketplaces =
    metadata && 'marketplaces' in metadata
      ? (metadata.marketplaces as MarketplaceIcons | undefined)
      : undefined;
  return marketplaces?.[key]?.icon ?? '';
};

export function CartoonProperties({ attributes, imperial = false }) {
  const {
    carton_gross_weight,
    carton_height,
    carton_length,
    carton_net_weight,
    carton_width,
    units_per_carton,
  } = attributes || {};

  const showLength = (value, unit) => {
    if (imperial) {
      return unit === 'inch'
        ? showNumber(value)
        : // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
          showNumber(CommonUtility.centiToInch(value, false));
    }

    if (!imperial) {
      return unit === 'inch'
        ? // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
          showNumber(CommonUtility.inchToCenti(value, false))
        : showNumber(value);
    }
  };

  const showWeight = (value, unit) => {
    if (!value) {
      return '-';
    }

    if (imperial) {
      const data =
        unit === 'lb'
          ? showNumber(value)
          : // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
            showNumber(CommonUtility.kiloToPound(value, false));
      return `${data} lb`;
    }

    if (!imperial) {
      const data =
        unit === 'lb'
          ? // @ts-expect-error - TS2554: Expected 1 arguments, but got 2.
            showNumber(CommonUtility.poundToKilo(value, false))
          : showNumber(value);
      return `${data} kg`;
    }
  };

  const renderUnitValue = data => {
    const { value, unit } = data || {};
    return <div className="sk:text-right">{showWeight(value, unit)}</div>;
  };

  return (
    <>
      <PropertyRow>
        <PropertyLabel>Carton Size</PropertyLabel>
        <div className="sk:text-right">
          {!carton_length ? (
            '-'
          ) : (
            <span>
              {[carton_length?.value, carton_width?.value, carton_height?.value]
                .map(d => showLength(d, carton_height?.unit))
                .join(' x ')}{' '}
              {imperial ? 'in.' : 'cm'}
            </span>
          )}
        </div>
      </PropertyRow>
      <PropertyRow>
        <PropertyLabel>Quantity Per Carton</PropertyLabel>
        <div className="sk:text-right">
          {units_per_carton
            ? CommonUtility.numberWithCommas(
                CommonUtility.getFixNumber(units_per_carton),
              )
            : '-'}
        </div>
      </PropertyRow>
      <PropertyRow>
        <PropertyLabel>Gross weight</PropertyLabel>
        {renderUnitValue(carton_gross_weight)}
      </PropertyRow>
      <PropertyRow>
        <PropertyLabel>Net weight</PropertyLabel>
        {renderUnitValue(carton_net_weight)}
      </PropertyRow>
    </>
  );
}

/* Sized to sit inline with an item title, beside CatalogElements' own badges. */
const INLINE_BADGE = 'sk:mr-1 sk:px-2 sk:py-0 sk:align-middle sk:text-xs';

/*
 * Callers only ever passed `bgColor` to flag a problem, so any value means the
 * danger tone. `minWidth` stays accepted; the badge sizes to its text.
 */
export function StatusBox({ status, minWidth: _minWidth = '', bgColor = '' }) {
  return (
    <Badge tone={bgColor ? 'danger' : 'neutral'} className={INLINE_BADGE}>
      {status}
    </Badge>
  );
}

export function BulkUploadHandler({
  bulkUploadClick,
  flat = false,
  disabled = false,
  position = '',
  buttonClass = '',
}) {
  // @ts-expect-error - TS2339: Property 'data' does not exist on type 'unknown'.
  const { data } = useSidebar();
  const { user, isSuperAdmin, isStarterPlan } = useAuth();
  const [loading, setLoading] = useState(false);

  const isSmallScreen = useMediaQuery({ query: '(max-width: 1240px)' });

  const catalogFile = useMemo(
    () => data?.general_sheets?.find(x => x.type === 'catalog') || {},
    [data],
  );

  const { actionDisabled, actionHidden, allowed } = useMemo(() => {
    const { modules } = user?.permissions?.Sheets ?? {};
    const { pathname } = window?.location ?? {};

    const permission = pathname.includes('app/catalogs')
      ? modules?.Catalog
      : modules?.['Supply Chain'];
    const actionDisabled = isActionDisabled(permission);
    const actionHidden = isActionHidden(permission);
    return {
      actionDisabled,
      actionHidden,
      allowed: (!actionDisabled && !actionHidden) || isSuperAdmin,
    };
  }, [user, isSuperAdmin]);

  const hitRefreshAPI = async sheet_link => {
    setLoading(true);
    try {
      await OrganizationsService.refreshGoogleSheets(sheetTypes.catalog);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
      window.open(sheet_link, '_blank', 'noreferrer');
    }
  };

  if (isStarterPlan) {
    return null;
  }

  const Button = flat ? FlatButton : HoverBorderButton;
  const hasSheet = !!catalogFile?.sheet_id;

  const onClick = hasSheet
    ? allowed
      ? () =>
          hitRefreshAPI(
            `${CommonConstant.googleSheetUrl}/${catalogFile.sheet_id}`,
          )
      : undefined
    : bulkUploadClick;

  const CTA = (
    <Button
      className={cn('sk:mx-2!', buttonClass)}
      size="mini"
      disabled={disabled || !allowed}
      loading={hasSheet ? loading : undefined}
      onClick={onClick}
      type="button"
    >
      Bulk Upload
    </Button>
  );

  return hasSheet ? (
    allowed ? (
      <Tooltip content={tooltipTexts.navBarSheet} className="sk:max-w-[500px]">
        {CTA}
      </Tooltip>
    ) : (
      <PermissionTooltip
        disabled={actionDisabled}
        hidden={actionHidden}
        position={position || (isSmallScreen ? 'bottom left' : 'bottom center')}
      >
        {CTA}
      </PermissionTooltip>
    )
  ) : (
    CTA
  );
}

export const extractMarketplaceErrors = (catalogDetails, added) => {
  const errors = [];
  Object.keys(catalogDetails.marketplaces).forEach(marketplace => {
    if (
      DYNAMIC_INFORMATION.includes(marketplace) &&
      MARKETPLACE_LIMITS[marketplace]
    ) {
      // The marketplace can have seperate information
      const { marketplace_id, connected } =
        catalogDetails.marketplaces[marketplace];
      if (!added.includes(marketplace_id) && connected) {
        const errorObject = {
          marketplaceName: marketplace,
          errors: [],
        };
        // The marketplace is not configured seperately
        const propertiesToTest = ['title', 'description'];
        propertiesToTest.forEach(property => {
          const itemKey = `p_${property}`;
          if (
            (catalogDetails[itemKey] || '').length >
            MARKETPLACE_LIMITS[marketplace]?.[property]
          ) {
            // @ts-expect-error - TS2345: Argument of type '{ propertyName: string; maxLength: any; }' is not assignable t...
            errorObject.errors.push({
              propertyName: property,
              maxLength: MARKETPLACE_LIMITS[marketplace]?.[property],
            });
          }
        });
        if (errorObject.errors.length > 0) {
          // @ts-expect-error - TS2345: Argument of type '{ marketplaceName: string; errors: never[]; }' is not assignab...
          errors.push(errorObject);
        }
      }
    }
  });
  return errors;
};

export const getConfiguredMarketplaces = catalogDetails => {
  const allAdded = [
    ...(CommonUtility.isValidArray(catalogDetails?.p_title_marketplace)
      ? CommonUtility.sortArrayObjectsByKey(
          catalogDetails?.p_title_marketplace,
          'marketplace_name',
        )
      : []),
    ...(CommonUtility.isValidArray(catalogDetails?.p_description_marketplace)
      ? CommonUtility.sortArrayObjectsByKey(
          catalogDetails?.p_description_marketplace,
          'marketplace_name',
        )
      : []),
  ];

  const sortedIds = CommonUtility.sortArrayObjectsByKey(
    allAdded,
    'marketplace_name',
  ).map(d => (d as { marketplace: string }).marketplace);

  return [...new Set(sortedIds)];
};

export const updateMarketplaceStatus = (syncItem, data, reqData) => {
  const { marketplaceKey } = syncItem;
  const temp = data.map(catalog => {
    const t = { ...catalog };
    if (t._id === syncItem.item._id) {
      const marketplaces = t.marketplaces || {};
      if (CommonUtility.isValidArray(marketplaces[marketplaceKey]?.shops)) {
        const updatedShops = marketplaces[marketplaceKey].shops.map(shop => {
          if (shop.marketplace_id === reqData.marketplace_id) {
            return {
              ...shop,
              integrate: reqData.integrate,
            };
          }
          return shop;
        });

        marketplaces[marketplaceKey] = {
          ...t.marketplaces[marketplaceKey],
          integrate: reqData.integrate,
          shops: updatedShops,
        };
      } else {
        marketplaces[marketplaceKey] = {
          ...t.marketplaces[marketplaceKey],
          integrate: reqData.integrate,
        };
      }
      t.marketplaces = marketplaces;
    }
    return t;
  });
  const newData = JSON.parse(JSON.stringify(temp));
  return newData;
};
